// The only file the rest of your site imports.
//
//   import ProjectsRoom from './components/ProjectsRoom'
//   <ProjectsRoom onSelect={(project) => navigate(project.href)} />
//
// This eager part is tiny (an IntersectionObserver, the SVG loader, some CSS). three.js
// and react-three-fiber live in Room.jsx, which is only fetched, mounted and rendered
// when the section is within ~900px of the viewport, and is paused when off-screen.
import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import Loader from './Loader'
import defaultProjects from './projects'
import { CONFIG } from './config'
import './ProjectsRoom.css'

const loadRoom = () => import('./Room')
const Room = lazy(loadRoom)

// Call this early (e.g. when the user hovers the "Work" link in your nav) to start
// downloading the 3D chunk before they scroll down.
export const preloadProjectsRoom = () => {
  loadRoom()
}

function webglAvailable() {
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const on = () => setReduced(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return reduced
}

// If WebGL is missing or the 3D chunk throws, show a plain grid of the same projects.
class RoomBoundary extends Component {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error) {
    console.error('[ProjectsRoom] falling back to the static grid:', error)
    this.props.onFail?.()
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

function StaticGrid({ projects, onSelect }) {
  return (
    <ul className="pr-static">
      {projects.map((p) => (
        <li key={p.id}>
          <a
            href={p.href}
            onClick={(e) => {
              if (onSelect) {
                e.preventDefault()
                onSelect(p)
              }
            }}
          >
            <img src={p.frames[0]} alt="" loading="lazy" />
            <span className="pr-static__kicker">{p.kicker}</span>
            <strong className="pr-static__title">{p.title}</strong>
            <span className="pr-static__summary">{p.summary}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}

export default function ProjectsRoom({ projects = defaultProjects, onSelect, height = '100vh', className = '' }) {
  const hostRef = useRef(null)
  const reduced = usePrefersReducedMotion()

  const [fallback, setFallback] = useState(() => typeof document !== 'undefined' && !webglAvailable())
  const [shouldMount, setShouldMount] = useState(false) // near the viewport at least once
  const [onScreen, setOnScreen] = useState(false) // currently visible
  const [sceneReady, setSceneReady] = useState(false) // chunk + textures + first mount done
  const [minPassed, setMinPassed] = useState(false) // loader has played long enough
  const [loaderGone, setLoaderGone] = useState(false)

  // Start loading when near; track whether it is actually on screen.
  useEffect(() => {
    const el = hostRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldMount(true)
      setOnScreen(true)
      return undefined
    }
    const near = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShouldMount(true)
          near.disconnect()
        }
      },
      { rootMargin: '900px 0px' },
    )
    const visible = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.01 })
    near.observe(el)
    visible.observe(el)
    return () => {
      near.disconnect()
      visible.disconnect()
    }
  }, [])

  // Keep the loader up for a minimum time, counted from when the section is first seen.
  const timerStarted = useRef(false)
  useEffect(() => {
    if (!shouldMount || !onScreen || timerStarted.current) return undefined
    timerStarted.current = true
    const id = setTimeout(() => setMinPassed(true), reduced ? 0 : CONFIG.loader.minMs)
    return () => clearTimeout(id)
  }, [shouldMount, onScreen, reduced])

  const handleReady = useCallback(() => setSceneReady(true), [])
  const handleFail = useCallback(() => setFallback(true), [])
  const handleGone = useCallback(() => setLoaderGone(true), [])

  const ready = sceneReady && minPassed

  return (
    <section
      ref={hostRef}
      className={`pr-host ${className}`}
      style={{ height }}
      data-ready={ready ? 'true' : 'false'}
      aria-label="Selected projects"
    >
      {fallback ? (
        <StaticGrid projects={projects} onSelect={onSelect} />
      ) : (
        <>
          {shouldMount && (
            <RoomBoundary onFail={handleFail}>
              <Suspense fallback={null}>
                <Room
                  projects={projects}
                  onSelect={onSelect}
                  // Render until the first frames are done (compiles shaders off-screen),
                  // then only while visible.
                  running={onScreen || !sceneReady}
                  reduced={reduced}
                  onReady={handleReady}
                />
              </Suspense>
            </RoomBoundary>
          )}
          {!loaderGone && <Loader done={ready} onGone={handleGone} />}
        </>
      )}
    </section>
  )
}
